import { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PresetButtonColors } from '@tinderapp/ui-react';
import classNames from 'classnames';

import config from 'Config';
import stopPropagation from 'Lib/utils/stopPropagation';

import TinderLogo from 'Containers/PageLayout/TinderLogoLink';
import ClickableDiv from 'Components/A11y/ClickableDiv';
import CloseX from 'Components/CloseX';
import { I13nAnchor } from 'Components/I13n';
import LanguagePicker from 'Components/LanguagePicker';
import Button from 'Components/Obsidian/Button';

import ArrowIcon from 'Assets/reactSvgs/ArrowIcon';

import HeaderMenuItem from '../lib/HeaderMenuItem';
import useMenuData from '../lib/useMenuData';

type Props = {
  className?: string;
  onClose: VoidFunction;
  onLoginClick?: MouseEventHandler;
  visible: boolean;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
};

const PageLayoutMobileHeaderMenu = ({
  className,
  onClose,
  onLoginClick,
  visible,
  menuButtonRef,
}: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { formatMessage } = useIntl();
  const menuData = useMenuData();

  const handleClose = useCallback(() => {
    onClose();
    if (menuButtonRef?.current) {
      menuButtonRef.current.focus();
    }
  }, [onClose, menuButtonRef]);

  const handleSelect = useCallback(
    (index) => () => {
      setSelectedIndex(index === selectedIndex ? null : index);
    },
    [selectedIndex]
  );

  useEffect(() => {
    if (visible && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [visible]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        className,
        'Expand Bgc($c-ds-background-primary) Trsdu($fast) D(f) Fxd(c) Jc(sb) Ta(start)',
        {
          'TranslateX(100%)': !visible,
        }
      )}
    >
      {visible && (
        <>
          <div className="Pos(r)">
            <TinderLogo className="W(100px) Mstart(20px) Mt(16px)" />
            <div className="Pos(a) T(0) End(0) Mt(16px) Mend(20px)">
              <CloseX
                aria-label={formatMessage({ id: 'close' })}
                data-testid="close"
                onClick={handleClose}
                withBorder={false}
                autoFocus
              />
            </div>
            <ul className="List(n) P(0)">
              {menuData.map((item, index) => (
                <li className="Px(10px) Mb(20px)" key={index}>
                  {item.items ? (
                    <ClickableDiv
                      className="P(10px) Bgc($c-ds-background-secondary) Fw($bold)"
                      onClick={handleSelect(index)}
                    >
                      <I13nAnchor
                        className={classNames({
                          'C($c-ds-text-brand-normal)': selectedIndex === index,
                        })}
                        href={item.href}
                        onClick={stopPropagation}
                        i13nModel={
                          item.href ? { label: 'MobileHeader', message: item.id } : undefined
                        }
                      >
                        {formatMessage({ id: item.id })}
                      </I13nAnchor>
                      {item.items?.length ? (
                        <ArrowIcon
                          className={classNames(
                            'Pos(a) End(20px) C($c-ds-text-secondary) Sq(20px) Trsdu($normal) Trsp($transform)',
                            {
                              'Rotate(90deg) C($c-ds-text-brand-normal)': selectedIndex === index,
                              'Rotate(270deg)': selectedIndex !== index,
                            }
                          )}
                          pathClassName=""
                        />
                      ) : null}
                    </ClickableDiv>
                  ) : (
                    <I13nAnchor
                      className={classNames(
                        'P(10px) Bgc($c-ds-background-secondary) Fw($bold) D(b) focus-button-style',
                        {
                          'C($c-ds-text-brand-normal)': selectedIndex === index,
                        }
                      )}
                      i13nModel={
                        item.href ? { label: 'MobileHeader', message: item.id } : undefined
                      }
                      href={item.href}
                    >
                      {formatMessage({ id: item.id })}
                    </I13nAnchor>
                  )}
                  {item?.items?.length && (
                    <HeaderMenuItem
                      menuItems={item.items}
                      hide={selectedIndex !== index}
                      className="Pstart(10px)"
                      itemClassName="P(10px)"
                      itemColorClassName="C($c-ds-text-secondary)"
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="P(28px) W(100%)">
            <Button
              {...PresetButtonColors.Primary}
              className="Mb(16px) W(100%) D(b)"
              onClick={onLoginClick}
              href={config.navMenuLinks.login}
              i13nModel={{ message: 'Login' }}
              data-testid="mobile-log-in"
            >
              {formatMessage({ id: 'LoginPage.logIn' })}
            </Button>
            <LanguagePicker className="Mx(a) My(16px) D(b) W(100%)" />
          </div>
        </>
      )}
    </div>
  );
};

export default PageLayoutMobileHeaderMenu;
